/**
 * ChatWithUserWeb.tsx - Web-specific chat messaging screen
 * Allows users to send and receive messages with other users
 */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '~contexts/ThemeContext';
import { useUserStore } from '~providers/userStore/zustand';
import FirebaseChatsServiceWeb from '~services/firebase-web/FirebaseChatsServiceWeb';
import { ChatTypeWithKey } from '~types/chat';
import { getTimestamp } from '~Utils/Time';
import moment from 'moment-timezone';
import { Dimensions } from 'react-native';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100vh',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid',
    gap: '12px',
    flexShrink: 0,
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
    flex: 1,
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    minHeight: 0,
  },
  messageGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    marginBottom: '12px',
  },
  messageBubble: {
    padding: '12px 16px',
    borderRadius: '12px',
    maxWidth: '70%',
    wordWrap: 'break-word' as const,
    fontSize: '14px',
    lineHeight: '1.4',
  },
  sentMessage: {
    color: 'white',
  },
  receivedMessage: {
    color: '#000',
  },
  timestamp: {
    fontSize: '12px',
    paddingHorizontal: '16px' as any,
    textAlign: 'center' as const,
    marginVertical: '8px' as any,
  },
  inputContainer: {
    display: 'flex',
    padding: '12px 16px',
    borderTop: '1px solid',
    gap: '8px',
    alignItems: 'flex-end',
  },
  input: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'none' as const,
    maxHeight: '100px',
  },
  sendButton: {
    background: '#007AFF',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    color: '#999',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    flexDirection: 'column' as const,
    gap: '12px',
    color: '#999',
  },
};

interface Message {
  id?: string;
  text?: string;
  message?: string;
  sender?: string;
  sender_name?: string;
  sender_id?: string;
  sender_image?: string;
  timestamp: string | number;
  type?: string;
  readUserIds?: string[];
}

interface ChatWithUserWebProps {
  chatId: string;
  onBack: () => void;
  onMessageSent?: () => void;  // Callback to refresh chat list after message sent
}

export default function ChatWithUserWeb({ chatId, onBack, onMessageSent }: ChatWithUserWebProps) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const user = useUserStore(state => state.user);
  const uid = user?.uid;

  const [chat, setChat] = useState<ChatTypeWithKey | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const optimisticMessagesRef = useRef<Set<string>>(new Set());
  const previousMessageCountRef = useRef<number>(0);
  const isUserScrollingRef = useRef<boolean>(false);

  // Subscribe to chat updates
  useEffect(() => {
    if (!uid || !chatId) {
      setIsLoading(false);
      return;
    }

    console.log('🔥 [WEB-CHAT] Setting up chat listener for:', chatId);

    const unsubscribe = FirebaseChatsServiceWeb.subscribeToSingleChat(
      chatId,
      (updatedChat: ChatTypeWithKey) => {
        console.log('✅ [WEB-CHAT] Chat updated:', updatedChat);
        setChat(updatedChat);
        if (updatedChat?.history) {
          // Merge real messages with optimistic messages
          setMessages(prevMessages => {
            const realMessages = updatedChat.history;
            const optimisticIds = optimisticMessagesRef.current;
            
            // Get only the optimistic messages that haven't been confirmed yet
            const unconfirmedOptimistic = prevMessages.filter(
              msg => {
                const msgId = (msg as any).id || (msg as any).message_id;
                return msgId && msgId.startsWith('temp-') && optimisticIds.has(msgId);
              }
            );
            
            // Combine: real messages + unconfirmed optimistic messages
            const combined = [...realMessages, ...unconfirmedOptimistic];
            
            // Remove duplicates by message_id (keep real version)
            const seen = new Set<string>();
            const deduplicated = combined.reverse().filter(msg => {
              const id = (msg as any).message_id || (msg as any).id;
              if (seen.has(id)) return false;
              seen.add(id);
              return true;
            }).reverse();
            
            console.log('📝 [WEB-CHAT] Merged messages:', {
              realCount: realMessages.length,
              optimisticCount: unconfirmedOptimistic.length,
              finalCount: deduplicated.length,
            });
            
            return deduplicated;
          });
        }
        setIsLoading(false);
      },
      (err: any) => {
        console.error('❌ [WEB-CHAT] Subscription error:', err);
        setError('Failed to load chat');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [uid, chatId]);

  // Auto-scroll only when new messages are added
  useEffect(() => {
    const hasNewMessages = messages.length > previousMessageCountRef.current;
    const isAtBottom = !isUserScrollingRef.current;

    // Only scroll if there are new messages and user hasn't manually scrolled up
    if (hasNewMessages && isAtBottom) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }

    previousMessageCountRef.current = messages.length;
  }, [messages]);

  // Detect when user scrolls
  useEffect(() => {
    const messagesContainer = document.querySelector('[data-messages-container]') as HTMLDivElement;
    if (!messagesContainer) return;

    const handleScroll = () => {
      // Check if user is at bottom (within 50px of bottom)
      const isAtBottom = 
        messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight < 50;
      isUserScrollingRef.current = !isAtBottom;
    };

    messagesContainer.addEventListener('scroll', handleScroll);
    return () => messagesContainer.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSendMessage = async () => {
    // Validate input
    if (!messageText.trim()) {
      return;
    }

    if (!chat || !uid) {
      setError('Chat or user information missing');
      return;
    }

    const tempId = `temp-${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const messageToSend = messageText.trim();
    
    // Create optimistic message
    const optimisticMessage: Message = {
      id: tempId,
      text: messageToSend,
      sender_id: uid,
      sender_name: user?.first_name || 'You',
      sender_image: user?.photo,
      timestamp: new Date().getTime(),
    };

    // Track this as an optimistic message
    optimisticMessagesRef.current.add(tempId);

    // Optimistic update - add message immediately
    setMessages(prev => [...prev, optimisticMessage]);
    setMessageText('');
    setIsSending(true);
    setError(null);

    try {
      console.log('📤 [WEB-CHAT] Sending message:', {
        chatId,
        userId: uid,
        userName: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
        text: messageToSend,
        tempMessageId: tempId,
      });

      // Check if user is blocked before sending (but NOT for support chats)
      if (chat?.type !== 'contact-pyxi') {
        const blockedUsers = chat.blockedUsers || {};
        const blockedBy = chat.blockedBy || {};

        const hasBlockedSomeone = Object.values(blockedUsers).some((v: any) => v === true);
        const isBlockedByOtherUser = Object.keys(blockedBy).includes(uid);

        if (hasBlockedSomeone) {
          throw new Error('You have blocked this user. Unblock them first to send messages.');
        }

        if (isBlockedByOtherUser) {
          throw new Error('This user has blocked you. Your message cannot be delivered.');
        }
      }

      // Send message using service - matches mobile implementation
      const messageId = await FirebaseChatsServiceWeb.pushMessageForUsers({
        chat_id: chatId,
        user_id: uid,
        user_image: user?.photo || '',
        user_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User',
        text: messageToSend,
        temp_message_id: tempId,
      });

      console.log('✅ [WEB-CHAT] Message sent successfully:', messageId);

      // Remove from optimistic tracking since it's now confirmed
      optimisticMessagesRef.current.delete(tempId);

      // Update the optimistic message with real message ID
      setMessages(prev =>
        prev.map(msg =>
          msg.id === tempId
            ? { ...msg, id: messageId, status: 'sent' }
            : msg
        )
      );
    } catch (err: any) {
      console.error('❌ [WEB-CHAT] Error sending message:', err);
      
      const errorMessage = err?.message || 'Failed to send message';
      setError(errorMessage);

      // Remove from optimistic tracking
      optimisticMessagesRef.current.delete(tempId);

      // Remove optimistic update on failure
      setMessages(prev => prev.filter(m => (m as any).id !== tempId));
      // Restore input text
      setMessageText(messageToSend);
    } finally {
      setIsSending(false);
    }
  };

  // Extract other user's name and image from chat data - matches ChatItem/ModernChatRow pattern
  const otherUserData = useMemo(() => {
    console.log('🔄 [otherUserData] Computing with chat:', chat, 'uid:', uid);
    
    if (!chat || !uid) {
      console.log('🔄 [otherUserData] Chat or uid missing, returning default');
      return { name: 'Chat', image: null };
    }

    // For event chats, use event name
    if (chat?.eventName) {
      console.log('🔄 [otherUserData] Event chat:', chat.eventName);
      return { name: chat.eventName, image: chat?.eventImage };
    }

    // For user chats, extract other user's info from chat history first
    const otherUserId = chat?.userIds?.find((userId: string) => userId !== uid);
    console.log('🔄 [otherUserData] otherUserId:', otherUserId, 'userIds:', chat?.userIds);
    
    const otherUserMessage = chat?.history?.find((message: any) => message.sender_id === otherUserId);
    console.log('🔄 [otherUserData] otherUserMessage:', otherUserMessage);
    
    const otherUserName = otherUserMessage?.sender;
    const otherUserImage = otherUserMessage?.sender_image;
    
    console.log('🔄 [otherUserData] otherUserName:', otherUserName, 'otherUserImage:', otherUserImage);

    // Fallback to users array if no message sender info
    if (!otherUserName && Array.isArray(chat.users)) {
      const userIndex = chat?.userIds?.findIndex((user: string) => user.toLowerCase() !== (uid || '').toLowerCase()) || 0;
      console.log('🔄 [otherUserData] Using users array. userIndex:', userIndex, 'users:', chat.users);
      return {
        name: chat.users[userIndex] || 'Chat',
        image: null,
      };
    }

    const result = {
      name: otherUserName || 'Chat',
      image: otherUserImage,
    };
    console.log('🔄 [otherUserData] Final result:', result);
    return result;
  }, [chat, uid]);

  const groupedMessages = useMemo(() => {
    if (!messages.length) return [];

    const groups: Array<{
      date: string;
      messages: Message[];
    }> = [];

    messages.forEach((msg, index) => {
      const msgDate = new Date(msg.timestamp).toLocaleDateString();
      const lastGroup = groups[groups.length - 1];

      if (lastGroup && lastGroup.date === msgDate) {
        lastGroup.messages.push(msg);
      } else {
        groups.push({ date: msgDate, messages: [msg] });
      }
    });

    return groups;
  }, [messages]);

  console.log('🔥 [ChatWithUserWeb] Rendering with otherUserData:', otherUserData, 'chat:', chat, 'isLoading:', isLoading);

  // Simple fallback for header
  const headerName = otherUserData?.name || 'Chat';
  const headerImage = otherUserData?.image || null;

  // Calculate header height
  const HEADER_HEIGHT = '60px';

  return (
    <div style={{ 
      ...styles.container, 
      backgroundColor: colors.background,
      position: 'relative',
      height: Dimensions.get('window').height - 50,
    }}>
      {/* Header - ABSOLUTE POSITION at top */}
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: `1px solid ${colors.border}`,
        gap: '12px',
        width: '100%',
        backgroundColor: colors.background,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: HEADER_HEIGHT,
        zIndex: 100,
      }}>
        <button
          style={{ 
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: colors.textPrimary,
          }}
          onClick={onBack}
          title="Back to chats"
        >
          ←
        </button>
        
        {/* Avatar */}
        <div style={{
          flexShrink: 0,
        }}>
          {headerImage ? (
            <img 
              src={headerImage} 
              alt={headerName}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '18px',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '18px',
              backgroundColor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '600',
              fontSize: '14px',
            }}>
              {headerName?.[0]?.toUpperCase() || '?'}
            </div>
          )}
        </div>

        <h2 style={{ 
          fontSize: '16px',
          fontWeight: '600',
          margin: 0,
          flex: 1,
          color: colors.textPrimary,
        }}>
          {headerName}
        </h2>
      </div>

      {/* Content Wrapper with marginTop to account for absolute header */}
      <div style={{
        ...styles.contentWrapper,
        marginTop: HEADER_HEIGHT,
      }}>
        {/* Messages */}
        {isLoading ? (
          <div style={{ ...styles.loadingContainer, color: colors.textSecondary }}>
            Loading chat...
          </div>
        ) : messages.length === 0 ? (
          <div style={{ ...styles.emptyState, color: colors.textSecondary }}>
            <p>No messages yet</p>
            <p style={{ fontSize: '12px' }}>Start the conversation!</p>
          </div>
        ) : (
          <div style={{ ...styles.messagesContainer, backgroundColor: colors.background }} data-messages-container>
            {groupedMessages.map((group, groupIdx) => (
              <div key={groupIdx} style={{ marginTop: groupIdx === 0 ? '8px' : 0 }}>
                {/* Date separator */}
                <div style={{ ...styles.timestamp, color: colors.textSecondary }}>
                  {new Date(group.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: new Date(group.date).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
                  })}
                </div>

                {/* Messages in this group */}
                {group.messages.map((msg, msgIdx) => {
                  const isSent = msg.sender_id === uid || (msg.sender_name === user?.first_name);
                  const msgText = msg.text || msg.message || '';
                  const msgTime = typeof msg.timestamp === 'number' 
                    ? new Date(msg.timestamp * 1000).toISOString()
                    : msg.timestamp;
                  const senderName = msg.sender_name || msg.sender || 'User';
                  
                  return (
                    <div
                      key={msg.id || msgIdx}
                      style={{
                        display: 'flex',
                        flexDirection: 'column' as const,
                        alignItems: isSent ? 'flex-end' : 'flex-start',
                        marginBottom: '8px',
                        gap: '2px',
                      }}
                    >
                      {/* Message bubble */}
                      <div
                        style={{
                          ...styles.messageBubble,
                          ...(isSent 
                            ? {
                                ...styles.sentMessage,
                                backgroundColor: colors.primary,
                              }
                            : {
                                ...styles.receivedMessage,
                                backgroundColor: colors.background === '#0F0F0F' ? '#2a2a2a' : '#f0f0f0',
                                color: colors.textPrimary,
                              }
                          ),
                          alignSelf: 'auto',
                          marginBottom: '0',
                        }}
                      >
                        {/* Show sender name for received messages - inside bubble */}
                        {!isSent && (
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: colors.primary,
                            marginBottom: '4px',
                          }}>
                            {senderName}
                          </div>
                        )}
                        {msgText}
                        <div
                          style={{
                            fontSize: '11px',
                            opacity: 0.7,
                            marginTop: '4px',
                          }}
                        >
                          {moment(msgTime).format('HH:mm')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: colors.error || '#ff3b30',
            color: 'white',
            fontSize: '13px',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0 8px',
            }}
            title="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      <div style={{ ...styles.inputContainer, backgroundColor: colors.background, borderTopColor: colors.border }}>
        <textarea
          style={{
            ...styles.input,
            borderColor: colors.border,
            color: colors.textPrimary,
            backgroundColor: colors.background === '#0F0F0F' ? '#2a2a2a' : '#fff',
          }}
          placeholder="Type a message..."
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          disabled={isSending}
        />
        <button
          style={{
            ...styles.sendButton,
            backgroundColor: colors.primary,
            opacity: isSending || !messageText.trim() ? 0.5 : 1,
            cursor: isSending || !messageText.trim() ? 'not-allowed' : 'pointer',
          }}
          onClick={handleSendMessage}
          disabled={isSending || !messageText.trim()}
        >
          {isSending ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
}
